// All sound is synthesised with Web Audio: the sea, autumn insects, a
// generative plucked score in the yo scale, and every contact sound. No files.
const YO = [0, 2, 5, 7, 9];            // D E G A B (relative to the root)
const IN = [0, 1, 5, 7, 8];            // tension: D Eb G A Bb
const ROOT = 62;                        // D4
const hz = midi => 440 * 2 ** ((midi - 69) / 12);
const scaleNote = (scale, step, root = ROOT) => root + scale[((step % 5) + 5) % 5] + 12 * Math.floor(step / 5);
// Rising sequences (chain chimes) fold back inside two octaves above `from`.
const fold = (step, from = 5, span = 10) => from + (((step - from) % span) + span) % span;

export class Audio {
  constructor() {
    this.enabled = true;
    this.ctx = null;
    this.musicOn = true;
    this.tension = 0;
  }

  // Must be called from a user gesture.
  start(offline = null) {
    if (this.ctx && !offline) { this.ctx.resume(); return; }
    const ctx = this.ctx = offline || new AudioContext();
    this.master = ctx.createGain(); this.master.gain.value = 0.9;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14; comp.ratio.value = 3; comp.attack.value = 0.004; comp.release.value = 0.2;
    this.master.connect(comp).connect(ctx.destination);
    this.sfx = ctx.createGain(); this.sfx.gain.value = 0.8; this.sfx.connect(this.master);
    this.music = ctx.createGain(); this.music.gain.value = 0.5; this.music.connect(this.master);
    this.amb = ctx.createGain(); this.amb.gain.value = 0.55; this.amb.connect(this.master);
    // Shared night-air reverb.
    this.verb = ctx.createConvolver();
    this.verb.buffer = this.impulse(2.8, 2.2);
    this.verbSend = ctx.createGain(); this.verbSend.gain.value = 0.5;
    this.verbSend.connect(this.verb).connect(this.master);
    this.noise = this.noiseBuffer(2);
    if (offline) return;   // measurement mode: effects only
    this.startAmbience();
    this.startMusic();
  }

  impulse(seconds, decay) {
    const ctx = this.ctx, n = Math.floor(ctx.sampleRate * seconds);
    const b = ctx.createBuffer(2, n, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = b.getChannelData(ch);
      for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, decay) * (i < 400 ? i / 400 : 1);
    }
    return b;
  }
  noiseBuffer(seconds) {
    const ctx = this.ctx, n = ctx.sampleRate * seconds;
    const b = ctx.createBuffer(1, n, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
    return b;
  }

  get now() { return this.ctx ? this.ctx.currentTime : 0; }
  live() { return this.ctx && this.enabled; }

  // ---- building blocks
  tone({ freq, type = 'sine', at = this.now, attack = 0.002, decay = 0.2, gain = 0.3, bus = this.sfx, verb = 0.2, glide = 0, pan = 0 }) {
    const ctx = this.ctx;
    const o = ctx.createOscillator(), g = ctx.createGain(), p = ctx.createStereoPanner();
    o.type = type; o.frequency.setValueAtTime(freq, at);
    if (glide) o.frequency.exponentialRampToValueAtTime(Math.max(30, freq * glide), at + attack + decay);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
    p.pan.value = pan;
    o.connect(g).connect(p).connect(bus);
    if (verb) { const s = ctx.createGain(); s.gain.value = verb; p.connect(s).connect(this.verbSend); }
    o.start(at); o.stop(at + attack + decay + 0.05);
  }
  burst({ at = this.now, decay = 0.05, gain = 0.3, filter = 'bandpass', freq = 2000, q = 1, bus = this.sfx, verb = 0.1, sweep = 0, pan = 0 }) {
    const ctx = this.ctx;
    const s = ctx.createBufferSource(); s.buffer = this.noise;
    const f = ctx.createBiquadFilter(); f.type = filter; f.frequency.setValueAtTime(freq, at); f.Q.value = q;
    if (sweep) f.frequency.exponentialRampToValueAtTime(Math.max(40, freq * sweep), at + decay);
    const g = ctx.createGain(), p = ctx.createStereoPanner();
    g.gain.setValueAtTime(gain, at); g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    p.pan.value = pan;
    s.connect(f).connect(g).connect(p).connect(bus);
    if (verb) { const v = ctx.createGain(); v.gain.value = verb; p.connect(v).connect(this.verbSend); }
    s.start(at, Math.random() * 1.5); s.stop(at + decay + 0.05);
  }
  // Plucked string (koto-like): bright attack, fast upper decay.
  pluck(midi, { at = this.now, gain = 0.18, decay = 1.6, bus = this.music, verb = 0.6, pan = 0 } = {}) {
    const f = hz(midi);
    this.tone({ freq: f, type: 'triangle', at, attack: 0.003, decay, gain, bus, verb, pan });
    this.tone({ freq: f * 2.01, type: 'sine', at, attack: 0.002, decay: decay * 0.35, gain: gain * 0.5, bus, verb, pan });
    this.tone({ freq: f * 3.02, type: 'sine', at, attack: 0.001, decay: decay * 0.12, gain: gain * 0.3, bus, verb: 0, pan });
    this.burst({ at, decay: 0.02, gain: gain * 0.4, freq: f * 4, q: 2, bus, verb: 0, pan });
  }
  bell(midi, { at = this.now, gain = 0.16, decay = 1.8, pan = 0 } = {}) {
    const f = hz(midi);
    [[1, 1], [2.76, 0.45], [5.4, 0.22], [8.93, 0.1]].filter(([m]) => f * m < 11000).forEach(([m, a]) => this.tone({ freq: f * m, at, attack: 0.002, decay: decay / Math.sqrt(m), gain: gain * a, verb: 0.55, pan }));
  }

  // ---- ambience
  startAmbience() {
    const ctx = this.ctx;
    // Surf: noise through a slow-swept lowpass with swelling gain.
    const src = ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 600;
    const g = ctx.createGain(); g.gain.value = 0.18;
    src.connect(lp).connect(g).connect(this.amb);
    src.start();
    this.surf = { lp, g };
    const swell = () => {
      if (!this.ctx) return;
      const t = this.now, len = 5 + Math.random() * 4;
      lp.frequency.setTargetAtTime(380 + Math.random() * 500, t, len * 0.3);
      g.gain.setTargetAtTime(0.1 + Math.random() * 0.14, t, len * 0.25);
      setTimeout(swell, len * 1000);
    };
    swell();
    // Autumn insects (鈴虫-like) far off: short high chirps in pairs.
    const chirp = () => {
      if (!this.ctx) return;
      if (this.enabled) {
        const t = this.now, f = 4100 + Math.random() * 500, pan = Math.random() * 1.6 - 0.8;
        for (let k = 0; k < 2 + (Math.random() * 3 | 0); k++) this.tone({ freq: f, at: t + k * 0.13, attack: 0.01, decay: 0.09, gain: 0.012, bus: this.amb, verb: 0.3, pan });
      }
      setTimeout(chirp, 1400 + Math.random() * 3800);
    };
    setTimeout(chirp, 2000);
  }

  // ---- generative score
  startMusic() {
    let bar = 0;
    const tick = () => {
      if (!this.ctx) return;
      if (this.enabled && this.musicOn) this.playBar(bar++);
      setTimeout(tick, this.barMs());
    };
    tick();
  }
  barMs() { return this.mode === 'title' ? 3600 : this.tension > 0.5 ? 2600 : 3200; }
  playBar(bar) {
    const t = this.now + 0.05, len = this.barMs() / 1000;
    const scale = this.tension > 0.5 ? IN : YO;
    const root = this.mode === 'boss' ? ROOT - 5 : ROOT;
    // Drone: root + fifth, very soft.
    if (bar % 2 === 0) {
      this.tone({ freq: hz(root - 24), type: 'sine', at: t, attack: 1.2, decay: len * 2, gain: 0.05, bus: this.music, verb: 0.4 });
      this.tone({ freq: hz(root - 17), type: 'sine', at: t + 0.3, attack: 1.4, decay: len * 2, gain: 0.03, bus: this.music, verb: 0.4 });
    }
    // Phrase: 3–6 plucks on a walking contour, sparse like someone playing by the water.
    const n = 3 + ((bar * 7) % 4);
    let step = 5 + ((bar * 3) % 4);
    for (let k = 0; k < n; k++) {
      if (Math.random() < 0.22) continue;
      step += [-1, 1, 1, -2, 2, -1][(bar + k * 5) % 6];
      const at = t + (k / n) * len * (0.75 + Math.random() * 0.1);
      this.pluck(scaleNote(scale, step, root), { at, gain: 0.08 + Math.random() * 0.05, pan: Math.random() * 0.6 - 0.3 });
    }
    if (this.tension > 0.5) {
      // Heartbeat of the returning tide.
      for (const o of [0, 0.22]) this.tone({ freq: 62, type: 'sine', at: t + o, attack: 0.005, decay: 0.25, gain: 0.16, bus: this.music, verb: 0.1, glide: 0.6 });
    }
  }
  setMode(mode) { this.mode = mode; }
  setTension(v) { this.tension = v; }

  // ---- gameplay sounds
  pullStart() {
    if (!this.live()) return;
    const ctx = this.ctx;
    const o = ctx.createOscillator(), g = ctx.createGain(), f = ctx.createBiquadFilter();
    o.type = 'sawtooth'; o.frequency.value = 110;
    f.type = 'lowpass'; f.frequency.value = 400; f.Q.value = 6;
    g.gain.value = 0.0001;
    o.connect(f).connect(g).connect(this.sfx);
    o.start();
    this.pull = { o, g, f };
  }
  pullUpdate(power) {
    if (!this.pull) return;
    const t = this.now;
    this.pull.o.frequency.setTargetAtTime(90 + power * 180, t, 0.03);
    this.pull.f.frequency.setTargetAtTime(300 + power * 1500, t, 0.03);
    this.pull.g.gain.setTargetAtTime(0.012 + power * 0.03, t, 0.03);
  }
  countIn(k) {
    if (!this.live()) return;
    this.tone({ freq: [1400, 1600, 1800, 2100][Math.min(k, 3)], at: this.now, decay: 0.05, gain: 0.35, verb: 0.15 });
    this.burst({ decay: 0.012, gain: 0.3, freq: 5000, verb: 0 });
  }
  pullDetent() { if (this.live()) { this.tone({ freq: 1800, at: this.now, decay: 0.03, gain: 0.5, verb: 0 }); this.burst({ decay: 0.015, gain: 0.6, freq: 5000, verb: 0 }); } }
  pullEnd() {
    if (!this.pull) return;
    const { o, g } = this.pull;
    g.gain.setTargetAtTime(0.0001, this.now, 0.02);
    o.stop(this.now + 0.15);
    this.pull = null;
  }
  release(power, scale = 1) {
    if (!this.live()) return;
    const g0 = this.sfx.gain.value;
    if (scale !== 1) { this.sfx.gain.setValueAtTime(g0 * scale, this.now); this.sfx.gain.setValueAtTime(g0, this.now + 0.3); }
    // パチン: a sharp click and a short plucked snap.
    this.burst({ decay: 0.03, gain: 1.6, filter: 'highpass', freq: 2500, verb: 0.05 });
    this.tone({ freq: 380 + power * 220, type: 'triangle', decay: 0.09, gain: 0.9, glide: 1.6, verb: 0.1 });
    this.tone({ freq: 140, type: 'sine', decay: 0.07, gain: 0.5 * power, glide: 0.6, verb: 0 }); // body thump
    this.burst({ at: this.now + 0.02, decay: 0.25, gain: 0.3, freq: 900, q: 0.7, sweep: 0.4, verb: 0.2 }); // water
  }
  stone(speed, pan) {
    if (!this.live()) return;
    // コッ: hollow stone/wood knock; brighter when fast.
    const k = Math.min(1, speed / 600);
    const f = 520 + k * 700 + Math.random() * 60;
    this.tone({ freq: f, type: 'sine', decay: 0.07, gain: 0.4 + k * 0.8, verb: 0.15, pan, glide: 0.85 });
    this.tone({ freq: f * 2.3, type: 'sine', decay: 0.03, gain: 0.15 + k * 0.25, verb: 0, pan });
    this.burst({ decay: 0.02, gain: 0.35 + k * 0.7, freq: 3000, q: 1.5, verb: 0, pan });
    if (k > 0.3) this.burst({ at: this.now + 0.01, decay: 0.18, gain: 0.18 * k, freq: 1400, q: 0.8, sweep: 0.5, verb: 0.2, pan });
  }
  clack(speed, pan) {
    if (!this.live()) return;
    // カチン: two shells, inharmonic, bright.
    const k = Math.min(1, speed / 600);
    for (const [m, a] of [[1, 1], [1.47, 0.6], [2.83, 0.35]]) this.tone({ freq: 1500 * m + Math.random() * 40, type: 'triangle', decay: 0.06 + k * 0.05, gain: (0.3 + k * 0.6) * a, verb: 0.25, pan });
    this.burst({ decay: 0.015, gain: 1.0 * k + 0.3, filter: 'highpass', freq: 4000, verb: 0, pan });
  }
  boing(speed, pan) {
    if (!this.live()) return;
    const k = Math.min(1, speed / 500);
    this.tone({ freq: 520 + k * 200, type: 'sine', decay: 0.16, gain: 0.3 + k * 0.3, glide: 0.55, verb: 0.2, pan });
    this.tone({ freq: 900, type: 'sine', at: this.now + 0.03, decay: 0.08, gain: 0.12, glide: 1.4, verb: 0.2, pan });
  }
  crossing(index, pan) {
    if (!this.live()) return;
    this.bell(scaleNote(YO, fold(8 + index, 8), ROOT), { gain: 0.07, decay: 1.0, pan });
  }
  penClose(index, size, allFour, saved = 1) {
    if (!this.live()) return;
    const t = this.now;
    if (!saved) {
      // Empty water lit: a soft shimmer only.
      this.burst({ at: t, decay: 0.7, gain: 0.05, filter: 'bandpass', freq: 3000, q: 0.6, sweep: 1.6, verb: 0.6 });
      this.bell(scaleNote(YO, fold(9 + index, 8), ROOT), { at: t, gain: 0.05, decay: 1.4 });
      return;
    }
    const big = Math.min(1, saved / 3 + (allFour ? 0.5 : 0));
    const base = fold(7 + index * 2, 5, 8);
    const notes = allFour ? [0, 2, 4, 5, 7, 9] : saved >= 3 ? [0, 2, 4, 5] : [0, 2, 4];
    notes.forEach((s, k) => this.bell(scaleNote(YO, base + s, ROOT), { at: t + 0.1 + k * 0.06, gain: 0.26 + big * 0.1, decay: 2.4 }));
    // Low "thoom" on the beat, then the airy swell as the light floods in.
    this.tone({ freq: 88, type: 'sine', at: t, attack: 0.005, decay: 0.32, gain: 0.55 + big * 0.3, glide: 0.75, verb: 0.2 });
    this.burst({ at: t + 0.08, decay: 0.9 + big, gain: 0.12 + big * 0.12, filter: 'bandpass', freq: 2400, q: 0.5, sweep: 2.2, verb: 0.6 });
    this.tone({ freq: hz(ROOT - 12 + YO[index % 5]), type: 'sine', at: t + 0.1, attack: 0.05, decay: 1.6, gain: 0.1 + 0.1 * big, bus: this.sfx, verb: 0.5 });
    this.duck(0.4, 1.4);
  }
  saved(k) {
    if (!this.live()) return;
    const t = this.now + k * 0.07;
    this.tone({ freq: hz(scaleNote(YO, fold(12 + k, 10), ROOT)), type: 'sine', at: t, decay: 0.25, gain: 0.14, glide: 1.25, verb: 0.5 });
  }
  undertow(cells) {
    if (!this.live()) return;
    const k = Math.min(1, cells / 120);
    this.burst({ decay: 1.6, gain: 0.08 + k * 0.12, filter: 'lowpass', freq: 500, q: 0.7, sweep: 0.3, verb: 0.3 });
    this.tone({ freq: 70, type: 'sine', attack: 0.2, decay: 1.4, gain: 0.08 + k * 0.1, glide: 0.7, verb: 0.2 });
    for (let i = 0; i < 4; i++) this.tone({ freq: 160 + Math.random() * 120, type: 'sine', at: this.now + 0.3 + i * 0.17, decay: 0.12, gain: 0.03, glide: 0.5, verb: 0.3 });
  }
  taken(k) {
    if (!this.live()) return;
    const t = this.now + 0.4 + k * 0.15;
    this.tone({ freq: hz(scaleNote(IN, 9 - k, ROOT)), type: 'triangle', at: t, decay: 0.6, gain: 0.08, glide: 0.5, verb: 0.6 });
  }
  hug() {
    if (!this.live()) return;
    this.burst({ decay: 0.25, gain: 0.8, filter: 'lowpass', freq: 1800, q: 4, sweep: 0.2, verb: 0.1 });
    this.tone({ freq: 240, type: 'sine', decay: 0.2, gain: 0.25, glide: 0.6 });
  }
  calm() {
    if (!this.live()) return;
    const t = this.now;
    this.tone({ freq: hz(ROOT - 24), type: 'sine', at: t, attack: 0.01, decay: 4, gain: 0.25, verb: 0.8 });
    this.tone({ freq: hz(ROOT - 24) * 2.4, type: 'sine', at: t, attack: 0.01, decay: 2.5, gain: 0.08, verb: 0.8 });
    [0, 2, 4, 7, 9, 12].forEach((s, k) => this.pluck(scaleNote(YO, 5 + s, ROOT), { at: t + 0.4 + k * 0.12, gain: 0.12, bus: this.sfx }));
    this.duck(0.2, 3);
  }
  poolClear() {
    if (!this.live()) return;
    const t = this.now;
    [0, 1, 2, 3, 4, 5, 7].forEach((s, k) => this.pluck(scaleNote(YO, 5 + s, ROOT), { at: t + k * 0.075, gain: 0.13, bus: this.sfx, decay: 1.8 }));
    this.bell(scaleNote(YO, 17, ROOT), { at: t + 0.6, gain: 0.1, decay: 2.6 });
    this.duck(0.3, 2);
  }
  thud(speed, pan) {
    if (!this.live()) return;
    const k = Math.min(1, speed / 500);
    this.tone({ freq: 140 + k * 60, type: 'sine', decay: 0.18, gain: 0.5 + k * 0.4, glide: 0.6, verb: 0.15, pan });
    this.burst({ decay: 0.06, gain: 0.25 + k * 0.3, filter: 'lowpass', freq: 900, verb: 0.05, pan });
  }
  vortex(pan) {
    if (!this.live()) return;
    this.burst({ decay: 0.5, gain: 0.25, filter: 'bandpass', freq: 600, q: 3, sweep: 3.5, verb: 0.4, pan });
    this.tone({ freq: 300, type: 'sine', decay: 0.4, gain: 0.12, glide: 2.2, verb: 0.4, pan });
  }
  horn() {
    if (!this.live()) return;
    // 法螺: a breathy low brass note with a fifth.
    const t = this.now;
    for (const [f, g] of [[hz(ROOT - 19), 0.3], [hz(ROOT - 12), 0.12]]) this.tone({ freq: f, type: 'sawtooth', at: t, attack: 0.12, decay: 0.9, gain: g * 0.4, verb: 0.5 });
    this.burst({ at: t, decay: 0.8, gain: 0.08, filter: 'bandpass', freq: 500, q: 1.5, verb: 0.4 });
  }
  pearl() {
    if (!this.live()) return;
    this.bell(scaleNote(YO, 14, ROOT), { gain: 0.18, decay: 1.2 });
    this.bell(scaleNote(YO, 16, ROOT), { at: this.now + 0.07, gain: 0.12, decay: 1.2 });
  }
  rival() {
    if (!this.live()) return;
    for (let i = 0; i < 6; i++) this.burst({ at: this.now + i * 0.07, decay: 0.03, gain: 0.25, filter: 'highpass', freq: 3000, verb: 0.05 });
    this.tone({ freq: 180, type: 'square', decay: 0.3, gain: 0.05, glide: 0.5, verb: 0.2 });
  }
  rain(on) {
    if (!this.ctx) return;
    if (on && !this.rainNode) {
      const s = this.ctx.createBufferSource(); s.buffer = this.noise; s.loop = true;
      const f = this.ctx.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = 1800;
      const g = this.ctx.createGain(); g.gain.value = 0.06;
      s.connect(f).connect(g).connect(this.amb); s.start();
      this.rainNode = { s, g };
    } else if (!on && this.rainNode) { this.rainNode.s.stop(); this.rainNode = null; }
  }
  ui() { if (this.live()) this.tone({ freq: 1200, type: 'sine', decay: 0.05, gain: 0.05, verb: 0.1 }); }
  duck(to, seconds) {
    if (!this.ctx) return;
    const g = this.music.gain, t = this.now;
    g.cancelScheduledValues(t); g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(0.5 * to, t + 0.05);
    g.linearRampToValueAtTime(0.5, t + seconds);
  }
}

export function vibrate(pattern) {
  try { if (navigator.vibrate && !matchMedia('(prefers-reduced-motion: reduce)').matches) navigator.vibrate(pattern); } catch { /* unsupported */ }
}
